import React from 'react';
import { cn } from './cn';

/**
 * KpiChip — compact metric tile for dashboards (label + value, optional icon
 * and trend delta). Token-backed so it themes automatically.
 *
 * Props:
 *  - label:   metric caption
 *  - value:   primary value (string | number)
 *  - icon:    optional lucide icon component
 *  - delta:   optional { value: string, direction: 'up' | 'down' | 'flat' }
 *  - footer:  optional React node below the value row (replaces inline delta layout)
 *  - accent:  'primary' | 'success' | 'warning' | 'danger' | 'info' — icon tint
 *  - align:   'center' | 'start' — vertical alignment when footer is multi-line
 *  - onClick: makes the whole chip a button
 */
const ACCENTS = {
  primary: 'text-ga-primary bg-ga-primary-soft',
  success: 'text-ga-success bg-ga-success/15',
  warning: 'text-ga-warning bg-ga-warning/15',
  danger:  'text-ga-danger bg-ga-danger/15',
  info:    'text-ga-info bg-ga-info/15',
};

const DELTA = {
  up:   'text-ga-success',
  down: 'text-ga-danger',
  flat: 'text-ga-text-muted',
};

export default function KpiChip({
  label,
  value,
  icon: Icon,
  delta,
  footer,
  accent = 'primary',
  align = 'center',
  onClick,
  className,
  ...props
}) {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'flex gap-3 rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface p-3 md:p-4 text-left shadow-ga-soft',
        align === 'start' ? 'items-start' : 'items-center',
        onClick && 'transition-colors hover:border-ga-primary/40 hover:bg-ga-bg-surface-subtle/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ga-focus',
        className
      )}
      {...props}
    >
      {Icon && (
        <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-ga-sm', ACCENTS[accent] || ACCENTS.primary)}>
          <Icon className="h-5 w-5" aria-hidden />
        </span>
      )}
      <div className={cn('min-w-0', footer && 'flex-1')}>
        <div className="text-[11px] font-medium uppercase tracking-wide text-ga-text-subtle truncate">{label}</div>
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-bold text-ga-text-primary leading-tight">{value}</span>
          {delta && !footer && (
            <span className={cn('text-xs font-semibold', DELTA[delta.direction] || DELTA.flat)}>
              {delta.value}
            </span>
          )}
        </div>
        {footer}
      </div>
    </Wrapper>
  );
}
